import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { AuthHeading, AuthLayout } from '@/features/auth/components/auth-layout'

/**
 * The reasons the sign-in callback can refuse, as the backend names them in the
 * `error` query parameter it redirects here with.
 *
 * Listed rather than interpolated so an unrecognised value -- including one
 * somebody typed -- falls through to the generic message instead of rendering a
 * missing-translation string, or the value itself, back to the user.
 */
const KNOWN_ERRORS = {
  errorUserDeactivated: 'loginError.errorUserDeactivated',
  errorProviderDoesNotExist: 'loginError.errorProviderDoesNotExist',
  errorAuthFlowError: 'loginError.errorAuthFlowError',
  errorDifferentProvider: 'loginError.errorDifferentProvider',
  errorSignupDisabled: 'loginError.errorSignupDisabled',
  errorEmailDomainNotAllowed: 'loginError.errorEmailDomainNotAllowed',
  errorTenantNotAllowed: 'loginError.errorTenantNotAllowed',
  errorTenantNeedsAdministrator: 'loginError.errorTenantNeedsAdministrator',
  errorSubscriptionRequired: 'loginError.errorSubscriptionRequired',
  errorUserSuspended: 'loginError.errorUserSuspended',
} as const

export type LoginErrorCode = keyof typeof KNOWN_ERRORS

/**
 * Deliberately offers no way back. Every reason this renders is settled -- the
 * provider has already authenticated the caller and refused them here -- and
 * `/login` now forwards to the provider without being clicked, so a way back
 * would be a loop against it at request rate rather than a second chance.
 */
export function LoginErrorPage() {
  const { t } = useTranslation()
  const { error } = useSearch({ from: '/login/error' })

  const key =
    error !== undefined && error in KNOWN_ERRORS
      ? KNOWN_ERRORS[error as LoginErrorCode]
      : 'loginError.defaultErrorMessage'

  return (
    <AuthLayout>
      <AuthHeading
        alert
        eyebrow={t('loginError.eyebrow')}
        heading={`${t('loginError.title')} ${t(key)}`}
      >
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t('loginError.help')}
        </p>
      </AuthHeading>
    </AuthLayout>
  )
}
